export function immutableSplice<T>(arr: Array<T>, start: number, deleteCount: number, ...items: Array<T>): Array<T> {
    return [...arr.slice(0, start), ...items, ...arr.slice(start + deleteCount)];
}

/**
 * Separators used when typing or pasting into tags-mode enum / multi-select Values inputs.
 * Intentionally excludes spaces so values like "New York" stay intact.
 */
export const TAG_VALUE_TOKEN_SEPARATORS = [",", "|", ";", "\n", "\r"] as const;

const DELIMITER_SPLIT_PATTERN = /[,|;\r\n]+/;
const DELIMITER_DETECT_PATTERN = /[,|;\r\n]/;

/**
 * Reports whether the given text contains any tag-value delimiter (comma, pipe, semicolon, or newline).
 *
 * @param text - The raw input text to inspect.
 * @returns `true` if a delimiter is present, otherwise `false`.
 */
export function hasTagValueDelimiter(text: string): boolean {
    return DELIMITER_DETECT_PATTERN.test(text);
}

/**
 * Parses clipboard/typed text into individual tag values.
 * Splits on commas, pipes, semicolons, and newlines; trims; drops empties.
 * Does not split on spaces.
 *
 * @param text - The raw input text to parse.
 * @returns The parsed, trimmed, non-empty tag values.
 */
export function parseDelimitedTagValues(text: string): string[] {
    if (!text) {
        return [];
    }

    return text
        .split(DELIMITER_SPLIT_PATTERN)
        .map((token) => token.trim())
        .filter((token) => token.length > 0);
}

/**
 * Appends incoming tags to existing ones, skipping exact duplicates.
 *
 * @param existing - The current tag values, whose order is preserved.
 * @param incoming - The new tag values to append when not already present.
 * @returns A new array combining existing and unique incoming values.
 */
export function mergeUniqueTagValues(existing: readonly string[], incoming: readonly string[]): string[] {
    const seen = new Set(existing);
    const merged = [...existing];

    for (const value of incoming) {
        if (!seen.has(value)) {
            seen.add(value);
            merged.push(value);
        }
    }

    return merged;
}
